import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import isArchived from 'archive/isArchived';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';

export type CanEditReturnValue = {canEdit: true; message: null} | {canEdit: false; message: string};

export function checkIsAnalysisArchived(
  analysis: Pick<PipelineGraphQL, 'id' | 'name'>,
  message?: string
): CanEditReturnValue | null {
  if (isArchived(analysis)) {
    return {
      canEdit: false,
      message: message ?? getArchivedTooltipMessage(analysis),
    };
  }
  return null;
}

/**
 * Check if a flowchart should show its edit functionality.
 *
 * @param analysis The analysis
 * @returns {shouldShow} False if the analysis is read-only or if it is archived but the user cannot unarchive it.
 */
export function showFlowchartEditFunctionality(
  analysis: Pick<PipelineGraphQL, 'permissions'> & MaybeArchivableItem
): boolean {
  const {writable, archivable, unarchivable} = analysis.permissions;
  const canChangeArchiveState = Boolean(archivable) || Boolean(unarchivable);
  // This is subtle but matters for published templates in the flowchart view. A published template has
  // writable = false but archivable = true, so checking archive permissions alone would wrongly show edit
  // controls. We also require `isArchived`: archive permission only justifies showing edit controls when the
  // item is actually archived (so the user can unarchive to edit). An archivable-but-not-archived item —
  // like a published template — therefore stays hidden.
  return writable || (isArchived(analysis) && canChangeArchiveState);
}
